import type {
  StylingReviewListQuery,
  StylingReviewMarkRequest,
} from '@cortex/domain-ac-mens-hub-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { rpcClient } from '../lib/orpc.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * 選定レビューの一覧取得・詳細取得・見立て保存。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 毎日の選定を1件ずつ確認して見立てを残す一連の操作を画面から駆動する。
 * @graph-connects rpcClient [calls] stylingReview 手続きの呼び出し
 */

/**
 * 一度に取得する選定の件数。
 * 1日約16件・全期間で2,043件（履歴とログの和集合。2026-08-28 実測）なので、既定の絞り込み（未確認）なら
 * これで数日ぶんの滞留まで一度に載る。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 一覧が一度に読み込む件数。数日ぶんの未確認をまとめて出せる大きさにする。
 * @graph-connects none
 */
export const STYLING_REVIEW_PAGE_SIZE = 100;

/**
 * 選定一覧。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 絞り込み条件つきで選定一覧と未確認件数を取得する。
 * @graph-connects rpcClient [calls] oRPC stylingReview.list 呼び出し
 */
export function useStylingReviewList(filter: StylingReviewListQuery['filter']) {
  return useQuery({
    queryKey: queryKeys.stylingReview.list(filter),
    queryFn: () => rpcClient.stylingReview.list({ filter, limit: STYLING_REVIEW_PAGE_SIZE }),
  });
}

/**
 * 選定1件の詳細。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 選定1件を判断するのに要る情報をまとめて取得する。
 * @graph-connects rpcClient [calls] oRPC stylingReview.detail 呼び出し
 */
export function useStylingReviewDetail(phoenixOrderId: number) {
  return useQuery({
    queryKey: queryKeys.stylingReview.detail(phoenixOrderId),
    queryFn: () => rpcClient.stylingReview.detail({ phoenixOrderId }),
  });
}

/**
 * 見立ての保存。
 *
 * 保存すると一覧側の未確認件数と既読状態が変わるため、一覧のキャッシュを丸ごと無効化する。
 * 絞り込みごとにキーが分かれているので、いま見ている絞り込みだけを消すと
 * 別の絞り込みに切り替えたときに古い未確認件数が出る。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 見立てを保存し、一覧の未確認件数と既読表示を最新にする。
 * @graph-connects rpcClient [calls] oRPC stylingReview.mark 呼び出し
 */
export function useSaveStylingReviewMark() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: StylingReviewMarkRequest) => rpcClient.stylingReview.mark(input),
    onSuccess: async (_result, input) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['stylingReview', 'list'] }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.stylingReview.detail(input.phoenixOrderId),
        }),
      ]);
    },
  });
}
